import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { auditApi } from '../services/api'
import type { AuditLog } from '../services/api'
import { 
  Calendar,
  User,
  Activity,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'

const AuditLogsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [limit] = useState(20)
  const [actionFilter, setActionFilter] = useState('')
  const [resourceTypeFilter, setResourceTypeFilter] = useState('')

  const { data: auditData, isLoading } = useQuery(
    ['audit-logs', page, limit, actionFilter, resourceTypeFilter],
    () => auditApi.list({
      page,
      limit,
      action: actionFilter || undefined,
      resourceType: resourceTypeFilter || undefined,
    }),
    { keepPreviousData: true }
  )

  const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return <Activity className="h-4 w-4 text-green-500" />
      case 'update':
        return <Settings className="h-4 w-4 text-blue-500" />
      case 'delete':
        return <FileText className="h-4 w-4 text-red-500" />
      case 'login':
        return <User className="h-4 w-4 text-purple-500" />
      default:
        return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const getActionBadgeColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'bg-green-100 text-green-800'
      case 'update':
        return 'bg-blue-100 text-blue-800'
      case 'delete':
        return 'bg-red-100 text-red-800'
      case 'login':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const formatResourceType = (resourceType?: string) => {
    if (!resourceType) return 'N/A'
    return resourceType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const totalPages = auditData ? Math.ceil(auditData.total / limit) : 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track all administrative actions and system changes
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="action-filter" className="block text-sm font-medium text-gray-700">
                Action
              </label>
              <select
                id="action-filter"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value)
                  setPage(1) // Reset to first page when filtering
                }}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="LOGIN">Login</option>
              </select>
            </div>

            <div>
              <label htmlFor="resource-filter" className="block text-sm font-medium text-gray-700">
                Resource Type
              </label>
              <select
                id="resource-filter"
                value={resourceTypeFilter}
                onChange={(e) => {
                  setResourceTypeFilter(e.target.value)
                  setPage(1) // Reset to first page when filtering
                }}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">All Resources</option>
                <option value="configuration">Configuration</option>
                <option value="admin_user">Admin User</option>
                <option value="auth">Authentication</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setActionFilter('')
                  setResourceTypeFilter('')
                  setPage(1)
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Logs List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Audit Trail
            </h3>
            {auditData && (
              <p className="text-sm text-gray-500">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, auditData.total)} of {auditData.total} entries
              </p>
            )}
          </div>
          
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : auditData?.logs && auditData.logs.length > 0 ? (
            <>
              <div className="space-y-4">
                {auditData.logs.map((log) => (
                  <div key={log.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadgeColor(log.action)}`}>
                              {log.action}
                            </span>
                            <span className="text-sm text-gray-500">
                              {formatResourceType(log.resourceType)}
                            </span>
                          </div>
                          <div className="mt-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">
                                {log.adminUser?.firstName} {log.adminUser?.lastName}
                              </span>
                              {log.adminUser && (
                                <span className="text-gray-500"> ({log.adminUser.email})</span>
                              )}
                              {' '}performed {log.action.toLowerCase()} action
                              {log.resourceType && ` on ${formatResourceType(log.resourceType)}`}
                            </p>
                          </div>
                          
                          {/* Show changes for UPDATE actions */}
                          {log.action === 'UPDATE' && (log.oldValues || log.newValues) && (
                            <div className="mt-3 space-y-2">
                              {log.oldValues && (
                                <div className="text-xs">
                                  <span className="font-medium text-gray-700">Previous:</span>
                                  <pre className="mt-1 p-2 bg-red-50 rounded text-red-800 overflow-x-auto">
                                    {JSON.stringify(log.oldValues, null, 2)}
                                  </pre>
                                </div>
                              )}
                              {log.newValues && (
                                <div className="text-xs">
                                  <span className="font-medium text-gray-700">New:</span>
                                  <pre className="mt-1 p-2 bg-green-50 rounded text-green-800 overflow-x-auto">
                                    {JSON.stringify(log.newValues, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Show new values for CREATE actions */}
                          {log.action === 'CREATE' && log.newValues && (
                            <div className="mt-3">
                              <div className="text-xs">
                                <span className="font-medium text-gray-700">Created:</span>
                                <pre className="mt-1 p-2 bg-green-50 rounded text-green-800 overflow-x-auto">
                                  {JSON.stringify(log.newValues, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {new Date(log.createdAt).toLocaleString()}
                            </div>
                            {log.ipAddress && (
                              <div>
                                IP: {log.ipAddress}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Page <span className="font-medium">{page}</span> of{' '}
                        <span className="font-medium">{totalPages}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => setPage(Math.max(1, page - 1))}
                          disabled={page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => setPage(Math.min(totalPages, page + 1))}
                          disabled={page === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No audit logs</h3>
              <p className="mt-1 text-sm text-gray-500">
                {actionFilter || resourceTypeFilter 
                  ? 'No logs match your current filters.'
                  : 'No audit logs have been recorded yet.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuditLogsPage
